import { type JSX, useState } from 'react';

import { alsEuro } from '../betrag';
import { heute } from '../datum';

/**
 * DIE BAUSTEINE DES STORNOS — im Service (Buchungsvorgang) und im Vertrieb
 * (Auftragsakte) dieselben.
 *
 * Bis Auftrag CRM-98 hatte jede Maske ihre eigene Fassung: Im Service stand
 * der Zugang als beschriftetes Feld mit Hilfetext, im Vertrieb als nacktes
 * Datumsfeld in der Knopfreihe; Herabsetzen und Rücknahme fragten im Service
 * über Eingabefenster des Browsers. Hier steht jede Angabe einmal.
 * Die Bestätigung läuft jetzt über `komponenten/Rueckfrage.tsx`, ein Dialog
 * der Anwendung und kein Eingabefenster des Browsers (CRM-103).
 */

/** BUC-144 (AeA-59): wie die Erklärung einging. „portal" setzt nur der Server. */
export const ERKLAERUNGSWEGE = ['email', 'brief', 'telefon', 'persoenlich', 'fax'] as const;
export type Erklaerungsweg = (typeof ERKLAERUNGSWEGE)[number];

export const ERKLAERUNGSWEGNAME: Readonly<Record<Erklaerungsweg | 'portal', string>> = {
  email: 'E-Mail',
  brief: 'Brief',
  telefon: 'Telefon',
  persoenlich: 'persönlich',
  fax: 'Fax',
  portal: 'Kundenportal',
};

/** Zugang, Weg und Verweis — so, wie die Felder sie halten. */
export type Erklaerungsangaben = {
  readonly zugang: string;
  readonly weg: Erklaerungsweg | '';
  readonly verweis: string;
};

export function neueErklaerung(): Erklaerungsangaben {
  return { zugang: heute(), weg: '', verweis: '' };
}

/** Die Felder für den Rumpf des Aufrufs; ohne Weg geht auch kein Verweis mit. */
export function erklaerungsrumpf(angaben: Erklaerungsangaben): {
  readonly erklaerungsweg?: Erklaerungsweg;
  readonly erklaerungsverweis?: string;
} {
  if (angaben.weg === '') return {};
  const verweis = angaben.verweis.trim();
  return verweis === ''
    ? { erklaerungsweg: angaben.weg }
    : { erklaerungsweg: angaben.weg, erklaerungsverweis: verweis };
}

/**
 * C7.6, BUC-144: Zugang der Erklärung, ihr Weg und ein Verweis darauf.
 *
 * Der Zugang entscheidet über die Stufe der Staffel; der Weg ist der Nachweis,
 * dass und wie sie einging (AeA-59).
 */
export function Erklaerungsfelder({
  id,
  wert,
  beiAenderung,
}: {
  /** Vorsilbe der Feldkennungen — eine Maske kann mehrere tragen. */
  readonly id: string;
  readonly wert: Erklaerungsangaben;
  readonly beiAenderung: (wert: Erklaerungsangaben) => void;
}): JSX.Element {
  return (
    <>
      <div className="feld">
        <label className="feld__beschriftung" htmlFor={`${id}-zugang`}>
          Zugang der Erklärung <span className="feld__pflicht">Pflicht</span>
        </label>
        <input
          id={`${id}-zugang`}
          type="date"
          className="feld__eingabe"
          value={wert.zugang}
          max={heute()}
          required
          onChange={(e) => beiAenderung({ ...wert, zugang: e.target.value })}
        />
        <p className="feld__hilfe">
          Wann die Erklärung eingegangen ist — nicht wann sie erfasst wird. Sie entscheidet über
          die Stufe der Staffel.
        </p>
      </div>
      <Herkunftsfelder
        id={id}
        weg={wert.weg}
        verweis={wert.verweis}
        beiAenderung={(h) => beiAenderung({ ...wert, ...h })}
      />
    </>
  );
}

/**
 * BUC-144 (AeA-59): wie die Erklärung einging und ein Verweis darauf — für
 * Masken, die den Zugang schon selbst führen (der Vorgang im Service).
 */
export function Herkunftsfelder({
  id,
  weg,
  verweis,
  beiAenderung,
}: {
  readonly id: string;
  readonly weg: Erklaerungsweg | '';
  readonly verweis: string;
  readonly beiAenderung: (wert: { readonly weg: Erklaerungsweg | ''; readonly verweis: string }) => void;
}): JSX.Element {
  return (
    <>
      <div className="feld">
        <label className="feld__beschriftung" htmlFor={`${id}-weg`}>
          Eingegangen per
        </label>
        <select
          id={`${id}-weg`}
          className="feld__eingabe"
          value={weg}
          onChange={(e) =>
            beiAenderung({
              weg: e.target.value as Erklaerungsweg | '',
              verweis: e.target.value === '' ? '' : verweis,
            })
          }
        >
          <option value="">— nicht angegeben —</option>
          {ERKLAERUNGSWEGE.map((w) => (
            <option key={w} value={w}>
              {ERKLAERUNGSWEGNAME[w]}
            </option>
          ))}
        </select>
      </div>
      <div className="feld">
        <label className="feld__beschriftung" htmlFor={`${id}-verweis`}>
          Notiz zur Erklärung
        </label>
        <input
          id={`${id}-verweis`}
          type="text"
          className="feld__eingabe"
          value={verweis}
          maxLength={500}
          disabled={weg === ''}
          onChange={(e) => beiAenderung({ weg, verweis: e.target.value })}
          placeholder="etwa Betreff der E-Mail oder Gesprächsnotiz"
        />
        <p className="feld__hilfe">Erst wählbar, wenn angegeben ist, wie die Erklärung einging.</p>
      </div>
    </>
  );
}

/** Ein Teilnehmerplatz zur Auswahl beim Teilstorno (C3.4). */
export type Platzzeile = {
  readonly id: string;
  readonly position: number;
  readonly name: string | null;
};

/**
 * BUC-51, C3.4: wie viele Plätze gehen und, auf Wunsch, wer.
 *
 * Die Auswahl erscheint erst, wenn nicht alle Plätze gehen — beim ganzen
 * Storno gibt es nichts zu wählen.
 */
export function Platzauswahl({
  id,
  gesamt,
  plaetze,
  beiPlaetze,
  teilnehmer,
  auswahl,
  beiAuswahl,
  legende = 'Wer nimmt nicht teil?',
}: {
  readonly id: string;
  readonly gesamt: number;
  readonly plaetze: string;
  readonly beiPlaetze: (wert: string) => void;
  readonly teilnehmer: readonly Platzzeile[];
  readonly auswahl: readonly string[];
  readonly beiAuswahl: (wert: readonly string[]) => void;
  readonly legende?: string;
}): JSX.Element {
  const anzahl = Number(plaetze);
  const teilweise = Number.isInteger(anzahl) && anzahl > 0 && anzahl < gesamt;
  return (
    <>
      <div className="feld">
        <label className="feld__beschriftung" htmlFor={`${id}-plaetze`}>
          Betroffene Plätze
        </label>
        <input
          id={`${id}-plaetze`}
          type="number"
          min={1}
          max={gesamt}
          className="feld__eingabe"
          value={plaetze}
          onChange={(e) => beiPlaetze(e.target.value)}
        />
        <p className="feld__hilfe">
          Von {gesamt} gebuchten. Gehen alle, fällt die ganze Position weg.
        </p>
      </div>
      {teilweise && teilnehmer.length > 0 && (
        <fieldset className="feld">
          <legend className="feld__beschriftung">{legende}</legend>
          {teilnehmer.map((tn) => (
            <label key={tn.id} className="liste__schalter">
              <input
                type="checkbox"
                checked={auswahl.includes(tn.id)}
                onChange={(e) =>
                  beiAuswahl(
                    e.target.checked ? [...auswahl, tn.id] : auswahl.filter((a) => a !== tn.id),
                  )
                }
              />
              <span>
                Platz {tn.position} — {tn.name ?? 'ohne Namen'}
              </span>
            </label>
          ))}
          <p
            className="feld__hilfe"
            role={auswahl.length > 0 && auswahl.length !== anzahl ? 'alert' : undefined}
          >
            {auswahl.length === 0
              ? 'Ohne Auswahl sind zuerst die Plätze ohne Namen betroffen, dann die mit der höchsten Nummer.'
              : auswahl.length === anzahl
                ? `${String(auswahl.length)} gewählt.`
                : `${String(auswahl.length)} gewählt — es müssen genau ${plaetze} sein.`}
          </p>
        </fieldset>
      )}
    </>
  );
}

/** Ob eine Platzauswahl vollständig ist: keine, oder genau so viele wie Plätze. */
export function auswahlStimmt(plaetze: string, auswahl: readonly string[]): boolean {
  return auswahl.length === 0 || auswahl.length === Number(plaetze);
}

/** Ein Betrag, wie der Server ihn annimmt: Punkt, höchstens zwei Nachkommastellen. */
export function alsBetragstext(eingabe: string): string | null {
  const text = eingabe.trim().replace(',', '.');
  return /^\d{1,9}(\.\d{1,2})?$/.test(text) ? text : null;
}

/**
 * BUC-146, BUC-148 (AeA-58, AeA-59): Gebühr herabsetzen oder Storno
 * zurücknehmen — als Formular an der Zeile, mit Pflichtgrund und eigenem
 * Bestätigungsknopf. Ob es geht (Beleg, Termin, freie Plätze), entscheidet
 * der Server und sagt warum; die Meldung steht dann über der Maske.
 */
export function Stornokorrektur({
  id,
  art,
  gebuehr,
  laeuft = false,
  mitRueckfrage = false,
  beiAbbrechen,
  beiBestaetigen,
}: {
  readonly id: string;
  readonly art: 'herabsetzen' | 'zuruecknehmen';
  /** Die bisher wirksame Gebühr; nur beim Herabsetzen. */
  readonly gebuehr?: string;
  readonly laeuft?: boolean;
  /** Der Knopf führt erst in eine Rückfrage und endet dann auf „ …" (CRM-82). */
  readonly mitRueckfrage?: boolean;
  readonly beiAbbrechen: () => void;
  readonly beiBestaetigen: (eingabe: { readonly betrag: string | null; readonly grund: string }) => void;
}): JSX.Element {
  const [betrag, setzeBetrag] = useState('');
  const [grund, setzeGrund] = useState('');
  const betragstext = alsBetragstext(betrag);
  const zuHoch =
    betragstext !== null && gebuehr !== undefined && Number(betragstext) > Number(gebuehr);
  const fertig =
    grund.trim() !== '' && (art === 'zuruecknehmen' || (betragstext !== null && !zuHoch));

  return (
    <div className="karte karte--zweit">
      <h3>{art === 'herabsetzen' ? 'Gebühr herabsetzen' : 'Storno zurücknehmen'}</h3>
      {art === 'herabsetzen' && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${id}-betrag`}>
            Neue Gebühr in Euro <span className="feld__pflicht">Pflicht</span>
          </label>
          <input
            id={`${id}-betrag`}
            className="feld__eingabe"
            inputMode="decimal"
            value={betrag}
            aria-invalid={(betrag !== '' && betragstext === null) || zuHoch || undefined}
            onChange={(e) => setzeBetrag(e.target.value)}
            placeholder="0,00"
          />
          <p className="feld__hilfe" role={zuHoch ? 'alert' : undefined}>
            {zuHoch
              ? `Höher als die bisherige Gebühr (${alsEuro(gebuehr ?? '0')}) — herabsetzen heißt senken.`
              : `Zwischen 0,00 € und ${alsEuro(gebuehr ?? '0')}. Geht nur, solange kein Beleg erzeugt ist.`}
          </p>
        </div>
      )}
      {art === 'zuruecknehmen' && (
        <p className="feld__hilfe">
          Die Plätze werden wieder belegt, die Gebühr entfällt, und ein Belegentwurf zum Storno
          wird verworfen. Das geht nur, solange kein Stornobeleg festgeschrieben ist, der Termin
          nicht begonnen hat und die Plätze noch frei sind.
        </p>
      )}
      <div className="feld">
        <label className="feld__beschriftung" htmlFor={`${id}-grund`}>
          Grund <span className="feld__pflicht">Pflicht</span>
        </label>
        <input
          id={`${id}-grund`}
          className="feld__eingabe"
          value={grund}
          maxLength={2000}
          onChange={(e) => setzeGrund(e.target.value)}
        />
      </div>
      <div className="knopfreihe knopfreihe--abschluss">
        <button type="button" className="knopf" onClick={beiAbbrechen}>
          Abbrechen
        </button>
        <button
          type="button"
          className="knopf knopf--haupt"
          disabled={!fertig || laeuft}
          onClick={() => beiBestaetigen({ betrag: betragstext, grund: grund.trim() })}
        >
          {art === 'herabsetzen' ? 'Herabsetzung bestätigen' : 'Rücknahme bestätigen'}
          {mitRueckfrage ? ' …' : ''}
        </button>
      </div>
    </div>
  );
}
